import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export type PlanMode = "days" | "sequential";

export const usePlanMode = (planId?: string) => {
  const queryClient = useQueryClient();

  const setMode = useMutation({
    mutationFn: async (mode: PlanMode) => {
      if (!planId) throw new Error("planId requerido");
      const { error } = await supabase
        .from("workout_plans")
        .update({ mode })
        .eq("id", planId);
      if (error) throw error;
      return mode;
    },
    onSuccess: async (mode) => {
      toast({
        title: "Modo actualizado",
        description: mode === "sequential"
          ? "El plan ahora funciona por orden secuencial"
          : "El plan ahora funciona por días de la semana",
        variant: "success",
      });
      // Invalidate + force refetch of active queries so UI updates immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["workout-plans"] }),
        queryClient.invalidateQueries({ queryKey: ["workout-plan", planId] }),
        queryClient.invalidateQueries({ queryKey: ["workout-plan-optimized", planId] }),
        queryClient.invalidateQueries({ queryKey: ["optimized-plan-routines", planId] }),
        queryClient.invalidateQueries({ queryKey: ["all-user-routines-with-days"] }),
      ]);
      await queryClient.refetchQueries({ type: "active" });
    },
    onError: (e) => {
      console.error("Error updating plan mode:", e);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo cambiar el modo del plan",
      });
    },
  });

  const reorderRoutines = useMutation({
    mutationFn: async (orderedRoutineIds: string[]) => {
      if (!planId) throw new Error("planId requerido");

      // Update positions one by one (small N, max 20). Throw on first error so the
      // dialog can surface it (and not silently activate sequential mode).
      const results = await Promise.all(
        orderedRoutineIds.map((routineId, idx) =>
          supabase
            .from("workout_plan_routines")
            .update({ position: idx })
            .eq("workout_plan_id", planId)
            .eq("routine_id", routineId)
        )
      );
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["optimized-plan-routines", planId] }),
        queryClient.invalidateQueries({ queryKey: ["workout-plan", planId] }),
        queryClient.invalidateQueries({ queryKey: ["workout-plan-optimized", planId] }),
        queryClient.invalidateQueries({ queryKey: ["all-user-routines-with-days"] }),
      ]);
      await queryClient.refetchQueries({ type: "active" });
    },
    onError: (e) => {
      console.error("Error reordering routines:", e);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo reordenar las rutinas",
      });
    },
  });

  return { setMode, reorderRoutines };
};
